import React, { useState } from 'react';
import { useClassName } from 'common/hooks';
import HomepageAsset from 'public/screens/Home/Components/Asset';
import { LinkTabs } from 'public/components/Tabs';
import { Link } from 'react-router-dom';
import { ExternalLink, SVGIcon } from 'common/components';
import { formatOpeningHours } from 'public/components/Header/HoursOfOperation/helpers';

import './stacking.less';
import useCustomStyle from '../../hooks';

export default function Stacking({ component, venue }) {
  const classNames = useClassName('CLPStacking');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [, style] = useCustomStyle(component.fields.settings);

  const slides = component.fields.items || [];

  const links = slides.map((slide) => ({
    url: '#',
    title: slide.fields.name,
  }));

  const renderMapLocation = (slide) => {
    const { address, googleMapsLink, appleMapsLink, screenshot } = slide.fields;

    let openingHoursString = venue?.openingHours
      ? formatOpeningHours(venue.openingHours)
      : '';
    if (openingHoursString.includes(':')) {
      openingHoursString = openingHoursString
        .split(':')[1]
        .trim()
        .toUpperCase();
    }

    return (
      <div className={classNames('slide__map-location')}>
        <div className={classNames('slide__map-location__info')}>
          <h3 className={classNames('slide__map-location__info__title')}>
            Location
          </h3>
          {address && (
            <div
              className={classNames('slide__map-location__info__description')}>
              <div
                className={classNames(
                  'slide__map-location__info__description__icon'
                )}>
                <SVGIcon name="pin-icon" />
              </div>
              <div
                className={classNames(
                  'slide__map-location__info__description__text'
                )}>
                <p>{address}</p>
              </div>
            </div>
          )}
          {openingHoursString && (
            <div
              className={classNames(
                'slide__map-location__info__opening-hours'
              )}>
              <h4>{venue?.name} Hours</h4>
              <p
                className={classNames(
                  'slide__map-location__info__opening-hours__text'
                )}>
                {openingHoursString}
              </p>
              {venue?.slug && (
                <p
                  className={classNames(
                    'slide__map-location__info__opening-hours__disclaimer'
                  )}>
                  *Opening hours may vary by establishment, please check our{' '}
                  <Link to={`/venue/${venue.slug}`}>directory</Link> to plan
                  your visit.
                </p>
              )}
            </div>
          )}
          <div className={classNames('slide__map-location__info__buttons')}>
            {googleMapsLink && (
              <ExternalLink
                href={googleMapsLink}
                className={classNames(
                  'slide__map-location__info__buttons__button'
                )}>
                <SVGIcon name="google-icon" /> View on Google Maps
              </ExternalLink>
            )}
            {appleMapsLink && (
              <ExternalLink
                href={appleMapsLink}
                className={classNames(
                  'slide__map-location__info__buttons__button'
                )}>
                <SVGIcon name="apple-icon" /> View on Apple Maps
              </ExternalLink>
            )}
          </div>
        </div>
        {screenshot && <HomepageAsset asset={screenshot} />}
      </div>
    );
  };

  const renderContent = (slide) => {
    const type = slide.sys.contentType.sys.id;

    if (type === 'lpFrame') {
      return (
        <div className={classNames('slide__frame')}>
          <iframe
            src={slide.fields.url}
            frameBorder="0"
            title={slide.fields.name}
          />
        </div>
      );
    }

    if (type === 'mapLocation') {
      return renderMapLocation(slide);
    }

    return (
      <div className={classNames('slide__asset')}>
        <HomepageAsset asset={slide} />
      </div>
    );
  };

  const tabActiveStyles = Object.fromEntries(
    Object.entries(style).filter(([key]) => key.includes('--tab-active-'))
  );
  const tabDefaultStyles = Object.fromEntries(
    Object.entries(style).filter(([key]) => key.includes('--tab-default-'))
  );

  return (
    <div className={classNames('container')}>
      {links.length > 1 && (
        <div className={classNames('links')}>
          <LinkTabs
            styles={{
              activeLink: tabActiveStyles,
              link: tabDefaultStyles,
              container: {
                backgroundColor: 'transparent',
              },
              linkClassName: classNames('link'),
            }}
            links={links}
            isSelected={(index) => index === selectedIndex}
            fixed={false}
            onSelect={(e, index) => {
              e.preventDefault();
              setSelectedIndex(index);
            }}
          />
        </div>
      )}
      <div className={classNames('slides')}>
        {slides.map((slide, index) => {
          const slideLength = slides.length;
          const depth = (index - selectedIndex + slideLength) % slideLength;
          const zIndex = slideLength - depth;
          const translateY = -depth * 70;
          const scale = 1 - depth * 0.091;
          const opacity = 1 - depth * 0.12;

          return (
            <div
              key={slide.sys.id}
              className={classNames('slide')}
              style={{
                zIndex,
                opacity,
                transform: `translateY(${translateY}px) scale(${scale})`,
              }}>
              {renderContent(slide)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
